import React, { useImperativeHandle, useCallback } from "react";
import type {
    MouseEventHandler,
    FocusEventHandler,
    ElementType,
    KeyboardEventHandler,
    PropsWithChildren,
} from "react";

import { Box, boxForwardRef } from "../../../../helpers/common/Box/Box";
import { KEY_CODE } from "../../../../utils/dom/keyCodeMapping";
import { useDropdownStore } from "../../store/store";
import { createDisplayNameForDropdownElement } from "../../utils/getDisplayName";
import { createTestIdForDropdownElement } from "../../utils/getTestId";

import type { BoxComponentWithDefault } from "../../../../helpers/common/Box/Box";
import type { PropsWithTsId } from "../../../../types/react";

export const DROPDOWN_BUTTON_TEST_ID = createTestIdForDropdownElement("button");

export interface IButton extends PropsWithChildren<Partial<PropsWithTsId>> {}

export const DropdownButton = boxForwardRef<
    BoxComponentWithDefault<"button", IButton>
>(
    (
        {
            as = "button",
            onClick,
            onMouseEnter,
            onKeyDown,
            onFocus,
            onBlur,
            "data-tsid": tsid = DROPDOWN_BUTTON_TEST_ID,
            ...props
        },
        ref,
    ) => {
        const {
            options: {
                openMenuAfterClick,
                openMenuAfterMouseEnter,
                openMenuAfterFocus,
                openByArrowRight,
                closeByArrowLeft,
                closeMenuAfterBlur,
            },
            buttonRef,
            isOpen,
            setIsOpen,
            isOurElement,
            isSubMenu,
            openMenu,
            openMenuByMouseEnter,
            closeMenu,
            focusToFirstElement,
        } = useDropdownStore();

        const openMenuOrFocusFirstElement = useCallback(() => {
            if (isOpen) {
                focusToFirstElement();
            } else {
                openMenu();
            }
        }, [focusToFirstElement, isOpen, openMenu]);

        const clickHandler: MouseEventHandler = useCallback(
            (e) => {
                if (openMenuAfterMouseEnter || !openMenuAfterClick) {
                    return;
                }
                setIsOpen((prevIsOpen) => {
                    if (prevIsOpen && openMenuAfterFocus) {
                        return prevIsOpen;
                    }
                    return !prevIsOpen;
                });
                onClick?.(e);
            },
            [
                openMenuAfterMouseEnter,
                openMenuAfterClick,
                setIsOpen,
                onClick,
                openMenuAfterFocus,
            ],
        );

        const keyDownHandler: KeyboardEventHandler = useCallback(
            (e) => {
                let needPreventEvent = false;
                switch (e.key) {
                    case KEY_CODE.ARROW_UP:
                        if (!isSubMenu) {
                            openMenuOrFocusFirstElement();
                        }
                        needPreventEvent = true;
                        break;
                    case KEY_CODE.ARROW_DOWN:
                        if (!isSubMenu) {
                            openMenuOrFocusFirstElement();
                        } else if (isOpen) {
                            focusToFirstElement();
                        }
                        needPreventEvent = true;
                        break;
                    case KEY_CODE.ARROW_RIGHT:
                        if (openByArrowRight) {
                            openMenuOrFocusFirstElement();
                            needPreventEvent = true;
                        }
                        break;
                    case KEY_CODE.ARROW_LEFT:
                        if (closeByArrowLeft) {
                            closeMenu();
                            needPreventEvent = true;
                        }
                        break;
                    default:
                        break;
                }

                if (needPreventEvent) {
                    e.preventDefault();
                }
                onKeyDown?.(e);
            },
            [
                closeByArrowLeft,
                closeMenu,
                focusToFirstElement,
                isOpen,
                isSubMenu,
                onKeyDown,
                openByArrowRight,
                openMenuOrFocusFirstElement,
            ],
        );

        const focusHandler: FocusEventHandler = useCallback(
            (e) => {
                if (openMenuAfterFocus) {
                    openMenu();
                }
                onFocus?.(e);
            },
            [openMenuAfterFocus, onFocus, openMenu],
        );

        const blurHandler: FocusEventHandler = useCallback(
            (e) => {
                /**
                 * Если окно осталось открытым (например, если closeByEscape=false), то закрываем окно, если фокус с кнопки
                 * перешёл на элемент, не относящийся к меню
                 */
                if (closeMenuAfterBlur && !isOurElement(e.relatedTarget)) {
                    closeMenu();
                }
                onBlur?.(e);
            },
            [closeMenuAfterBlur, isOurElement, onBlur, closeMenu],
        );

        const mouseEnterHandler: MouseEventHandler = useCallback(
            (e) => {
                openMenuByMouseEnter();
                onMouseEnter?.(e);
            },
            [openMenuByMouseEnter, onMouseEnter],
        );
        /** Устанавливаем ref, полученному от родителя, значение от buttonRef.current */
        useImperativeHandle(ref, () => buttonRef.current);

        return (
            <Box
                {...props}
                as={as as ElementType}
                onClick={clickHandler}
                onKeyDown={keyDownHandler}
                onFocus={focusHandler}
                onBlur={blurHandler}
                onMouseEnter={mouseEnterHandler}
                aria-haspopup="true"
                aria-expanded={isOpen}
                data-tsid={tsid}
                ref={buttonRef}
            />
        );
    },
);

DropdownButton.displayName = createDisplayNameForDropdownElement("Button");

export default DropdownButton;
